// Durable, offline-first throw queue.
//
// Solo/ghost games advance LOCALLY and optimistically (see gamePhase.ts
// — projectThrow gives the next state with no network round-trip), then
// the throw is enqueued here for eventual persistence to the server.
// The queue:
//   * survives reloads / app restarts (localStorage), so a game played
//     fully offline syncs the next time the device is online;
//   * preserves per-game ORDER (the server replays events in seq order,
//     and round-commit math depends on it);
//   * is idempotent — each entry carries the clientEventUuid the server
//     dedupes on, so a retried or double-flushed POST commits exactly
//     once.
//
// Remote 1v1/2v2 games do NOT use this path — they stay strictly
// server-authoritative (await the POST, settle on the returned snapshot)
// because two devices must agree on state.

import { api } from "@/lib/api";

const STORAGE_KEY = "cornholio.throwQueue.v1";
// Background flush cadence while the app is open + online. The queue
// also flushes immediately on enqueue and on the `online` event; this
// interval is the backstop that drains anything left after a transient
// failure.
const FLUSH_INTERVAL_MS = 15_000;

export type QueuedThrow = {
  clientEventUuid: string;
  outcome: "in_hole" | "on_board" | "miss";
  landing?: { x: number; y: number; z: number };
};

type QueueMap = Record<string, QueuedThrow[]>;

function readQueue(): QueueMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as QueueMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeQueue(q: QueueMap): void {
  if (typeof window === "undefined") return;
  try {
    // Drop empty per-game arrays so the blob doesn't grow forever.
    const trimmed: QueueMap = {};
    for (const [gameId, arr] of Object.entries(q)) {
      if (arr.length > 0) trimmed[gameId] = arr;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage full / unavailable — non-fatal. The throw already
    // applied locally; persistence will simply be best-effort.
  }
}

// Total pending throws across all games. Drives an optional "syncing N"
// affordance.
export function pendingThrowCount(): number {
  const q = readQueue();
  let n = 0;
  for (const arr of Object.values(q)) n += arr.length;
  return n;
}

// Append a throw to a game's queue and kick a flush attempt. Safe to
// call while offline — it just persists and the flusher picks it up
// when connectivity returns.
export function enqueueThrow(gameId: string, t: QueuedThrow): void {
  const q = readQueue();
  const arr = q[gameId] ?? [];
  // Dedupe by uuid in case the same throw is enqueued twice (StrictMode
  // double-invoke, retry, etc.).
  if (!arr.some((e) => e.clientEventUuid === t.clientEventUuid)) {
    arr.push(t);
  }
  q[gameId] = arr;
  writeQueue(q);
  void flushThrowQueue();
}

let flushing = false;

// Drain every game's queue in order. One in-flight flush at a time so
// concurrent triggers (enqueue + interval + online) don't double-POST.
// Stops a game's drain at the first failure and leaves the rest queued
// — preserving order — to retry on the next tick.
export async function flushThrowQueue(): Promise<void> {
  if (typeof window === "undefined") return;
  if (flushing) return;
  if (!navigator.onLine) return;
  flushing = true;
  try {
    let q = readQueue();
    for (const gameId of Object.keys(q)) {
      const arr = q[gameId] ?? [];
      while (arr.length > 0) {
        const next = arr[0]!;
        try {
          await api(`/games/${gameId}/throws`, {
            method: "POST",
            body: {
              clientEventUuid: next.clientEventUuid,
              outcome: next.outcome,
              ...(next.landing ? { landing: next.landing } : {}),
            },
          });
        } catch {
          // Network or transient server error — stop draining THIS game
          // (keep order) and move on; the interval / online handler
          // retries later. A permanent 4xx would also land here; the
          // entry stays queued, but the local game already reflects the
          // throw, so the user is never blocked.
          break;
        }
        // Success (or idempotent dedupe) — remove and persist progress
        // immediately so a crash mid-drain doesn't replay committed
        // throws.
        arr.shift();
        q = readQueue();
        q[gameId] = arr;
        writeQueue(q);
      }
    }
  } finally {
    flushing = false;
  }
}

let installed = false;

// Install the background flusher once at app boot. Flushes on a steady
// interval, whenever the network comes back online, and when the tab
// regains focus (mobile resumes).
export function installThrowQueueFlusher(): void {
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;
  void flushThrowQueue();
  setInterval(() => void flushThrowQueue(), FLUSH_INTERVAL_MS);
  window.addEventListener("online", () => void flushThrowQueue());
  window.addEventListener("focus", () => void flushThrowQueue());
}
